import { useMemo } from "react"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import { BinaryOperator, Query } from "@cubejs-client/core"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import dayjs from "dayjs"
import useCubeFiltersFromColumnFilters from "~/ui-rtk/hooks/cube-filters-from-column-filters"
import { type TColumnFiltersState } from "~/ui-rtk/constants/table-visualization-types"
import { TSortingState } from "~/ui-rtk/hooks/url-sorting"

export type TCreativeReport =
  | "creative_id"
  | "media_id"
  | "story_id"
  | "object_story_id"
  | "root_ad_name"
  | "video_id"

export const CREATIVE_REPORT: Record<string, TCreativeReport> = {
  CREATIVE_ID: "creative_id",
  MEDIA_ID: "media_id",
  STORY_ID: "story_id",
  OBJECT_STORY_ID: "object_story_id",
  ROOT_AD_NAME: "root_ad_name",
  VIDEO_ID: "video_id",
}

export const CREATIVE_REPORT_OPTIONS: {
  label: string
  value: TCreativeReport
  primaryId: string
  skipPrimaryId?: boolean
}[] = [
  {
    label: "Root Ad Name",
    value: CREATIVE_REPORT.ROOT_AD_NAME,
    primaryId: "root_ad_name",
  },
  {
    label: "Creative ID",
    value: CREATIVE_REPORT.CREATIVE_ID,
    primaryId: "creative_id",
  },
  {
    label: "Media ID",
    value: CREATIVE_REPORT.MEDIA_ID,
    primaryId: "media_id",
  },
  {
    label: "Story ID",
    value: CREATIVE_REPORT.STORY_ID,
    primaryId: "story_id",
  },
  {
    label: "Object Story ID",
    value: CREATIVE_REPORT.OBJECT_STORY_ID,
    primaryId: "object_story_id",
  },
  {
    label: "Video ID",
    value: CREATIVE_REPORT.VIDEO_ID,
    primaryId: "video_id",
  },
]

export const CREATIVE_REPORTS_METRICS_MAP = {
  // Brand metrics
  brand_value_agg: "Brand Value",
  d30_brand_value_agg: "Brand Value 30d",
  d60_brand_value_agg: "Brand Value 60d",
  d90_brand_value_agg: "Brand Value 90d",
  d120_brand_value_agg: "Brand Value 120d",
  d150_brand_value_agg: "Brand Value 150d",
  brand_value_over_spend: "Brand Value ROAS",
  d30_brand_value_over_spend: "Brand Value 30d ROAS",
  d60_brand_value_over_spend: "Brand Value 60d ROAS",
  d90_brand_value_over_spend: "Brand Value 90d ROAS",
  d120_brand_value_over_spend: "Brand Value 120d ROAS",
  d150_brand_value_over_spend: "Brand Value 150d ROAS",
  estimated_d180_brand_value: "Modeled Brand Value",
  modeled_brand_value_over_spend: "Modeled Brand ROAS",
  modeled_revenue: "Modeled Revenue",
  modeled_revenue_over_spend: "Modeled DR ROAS",

  // Aggregated engagement metrics
  comments: "Comments",
  likes: "Likes",
  shares: "Shares",
  follows: "Follows",
  saves: "Saves",
  reach: "Reach",

  // Aggregated revenue metrics
  purchases: "Purchases",
  purchase_value: "Purchase Value",
  spend: "Spend",
  ga4_total_revenue: "GA4 Total Revenue",
  shopify_first_total_revenue: "Shopify First Total Revenue",
  shopify_last_total_revenue: "Shopify Last Total Revenue",
  ga4_estimated_revenue_last_click: "GA4 Estimated Revenue",
  shopify_last_estimated_total_revenue: "Shopify Last Estimated Revenue",
  shopify_first_estimated_total_revenue: "Shopify First Estimated Revenue",

  // ROAS metrics
  purchase_value_over_spend: "Purchase ROAS",
  total_value: "Total Value",
  ga4_total_revenue_over_spend: "GA4 Revenue ROAS",

  // Ad set / campaign / ad coverage
  distinct_ad_count: "Distinct Ads",
  distinct_adset_count: "Distinct Ad Sets",
  distinct_campaign_count: "Distinct Campaigns",

  // Aggregated metadata
  md_image_url: "Image URL",
  media_permalink_url: "Media Permalink URL",

  // Aggregated name arrays (where spend > 0)
  all_ad_names: "All Ad Names",
  all_adset_names: "All Ad Set Names",
  all_campaign_names: "All Campaign Names",

  // Count of distinct names (where spend > 0)
  distinct_ad_names_with_spend: "Distinct Ad Names with Spend",
  distinct_adset_names_with_spend: "Distinct Ad Set Names with Spend",
  distinct_campaign_names_with_spend: "Distinct Campaign Names with Spend",

  // Active counts
  active_ads: "Active Ads",
  active_adsets: "Active Ad Sets",
  active_campaigns: "Active Campaigns",

  // Serving percentages
  serving_ads_percent_of_total: "Serving Ads %",
  serving_adsets_percent_of_total: "Serving Ad Sets %",
  serving_campaigns_percent_of_total: "Serving Campaigns %",

  // Ad set coverage (distinct_adset_count / active_adsets * 100)
  adset_coverage: "Ad Set Coverage %",
}

// Categorize metrics by group
export const BRAND_METRICS: TMetricsMapKey[] = [
  "brand_value_agg",
  "d30_brand_value_agg",
  "d60_brand_value_agg",
  "d90_brand_value_agg",
  "d120_brand_value_agg",
  "d150_brand_value_agg",
  "brand_value_over_spend",
  "d30_brand_value_over_spend",
  "d60_brand_value_over_spend",
  "d90_brand_value_over_spend",
  "d120_brand_value_over_spend",
  "d150_brand_value_over_spend",
  "estimated_d180_brand_value",
  "modeled_brand_value_over_spend",
]

export const REVENUE_METRICS: TMetricsMapKey[] = [
  "purchases",
  "purchase_value",
  "spend",
  "modeled_revenue",
  "modeled_revenue_over_spend",
  "ga4_total_revenue",
  "shopify_first_total_revenue",
  "shopify_last_total_revenue",
  "ga4_estimated_revenue_last_click",
  "shopify_last_estimated_total_revenue",
  "shopify_first_estimated_total_revenue",
  "purchase_value_over_spend",
  "total_value",
  "ga4_total_revenue_over_spend",
]

export const ENGAGEMENT_METRICS: TMetricsMapKey[] = [
  "comments",
  "likes",
  "shares",
  "follows",
  "saves",
  "reach",
]

export const PERFORMANCE_METRICS: TMetricsMapKey[] = [
  "distinct_ad_count",
  "distinct_adset_count",
  "distinct_campaign_count",
  "distinct_ad_names_with_spend",
  "distinct_adset_names_with_spend",
  "distinct_campaign_names_with_spend",
  "active_ads",
  "active_adsets",
  "active_campaigns",
  "serving_ads_percent_of_total",
  "serving_adsets_percent_of_total",
  "serving_campaigns_percent_of_total",
  "adset_coverage",
  "all_ad_names",
  "all_adset_names",
  "all_campaign_names",
]

export const ALL_METRICS = Object.keys(CREATIVE_REPORTS_METRICS_MAP)

export type TMetricsMapKey = keyof typeof CREATIVE_REPORTS_METRICS_MAP

export const ALWAYS_METRICS: TMetricsMapKey[] = ["spend", "brand_value_agg"]

export const DEFAULT_METRICS: TMetricsMapKey[] = [
  "brand_value_over_spend",
  "purchase_value",
  "purchase_value_over_spend",
  "ga4_total_revenue",
]

export const COEF_METRICS: TMetricsMapKey[] = [
  "brand_value_over_spend",
  "d30_brand_value_over_spend",
  "d60_brand_value_over_spend",
  "d90_brand_value_over_spend",
  "d120_brand_value_over_spend",
  "d150_brand_value_over_spend",
  "purchase_value_over_spend",
  "total_value",
  "ga4_total_revenue_over_spend",
  "modeled_revenue_over_spend",
  "modeled_brand_value_over_spend",
]

export const PERCENTAGE_METRICS: TMetricsMapKey[] = [
  "serving_ads_percent_of_total",
  "serving_adsets_percent_of_total",
  "serving_campaigns_percent_of_total",
  "adset_coverage",
]

export const MONETARY_METRICS: TMetricsMapKey[] = [
  "brand_value_agg",
  "d30_brand_value_agg",
  "d60_brand_value_agg",
  "d90_brand_value_agg",
  "d120_brand_value_agg",
  "d150_brand_value_agg",
  "purchase_value",
  "spend",
  "ga4_total_revenue",
  "shopify_first_total_revenue",
  "shopify_last_total_revenue",
  "ga4_estimated_revenue_last_click",
  "shopify_last_estimated_total_revenue",
  "shopify_first_estimated_total_revenue",
  "total_value",
  "estimated_d180_brand_value",
  "modeled_revenue",
]

// Default pagination size: 12 items per page for optimal grid layout (4 columns × 3 rows)
export const ITEMS_PER_PAGE = 12

// Internal cube measures only requested in aggregated mode (Group by Media
// Channel off). They are not user-selectable columns; they provide the channel
// list and the highest-spend representative creative for the card image + Blast.
export const AGG_ONLY_MEASURES = [
  "all_media_channels",
  "top_spend_creative_id",
  "top_spend_media_channel",
  "top_spend_image_url",
  "top_meta_creative_id",
  "top_meta_object_story_id",
  "top_meta_ad_id",
  "top_meta_destination_url",
]

function getQWithTimeDimensionAndDepVarFilters(
  q: Query,
  prefix: string,
  dateRange: [Date, Date],
  {
    depVar,
    depVarScaler,
  }: {
    depVar: string
    depVarScaler: string[]
  },
) {
  const filters = [
    {
      dimension: `${prefix}.dep_var`,
      operator: "equals" as BinaryOperator,
      values: [depVar],
    },
  ]
  if (depVarScaler.length > 0) {
    filters.push({
      dimension: `${prefix}.dep_var_scaler`,
      operator: "equals" as BinaryOperator,
      values: depVarScaler,
    })
  }
  return polyfillCubeQueryWithTimeDimension(
    {
      ...q,
      timeDimensions: [
        {
          dimension: `${prefix}.md_date`,
        },
      ],
      filters,
    },
    {
      dateRange,
    },
  )
}

const getBaseQuery = (prefix: string, metrics: TMetricsMapKey[]) => ({
  measures: [
    ...metrics.filter(metric => !!metric).map(metric => `${prefix}.${metric}`),
  ],
})

export default function useConnect(
  source: TCreativeReport,
  dateRange: [Date, Date],
  page: number,
  columnFilters: TColumnFiltersState | undefined,
  sorting: TSortingState | undefined,
  campaignNameFilter?: string,
  adsetNameFilter?: string,
  groupByMediaChannel = true,
) {
  const { depVar, depVarScaler, amazonEnabled, toggleAmazon } = useDepVar()

  // Build cube query
  const cubeQuery = useMemo(() => {
    const prefix = `company_media_metrics_ads_creative_report`
    const cubeFilters = useCubeFiltersFromColumnFilters(prefix, columnFilters)

    // Build unique dimensions array. In grouped (default) mode we include the
    // creative identifiers needed for per-channel cards + blast. In aggregated
    // mode (Group by Media Channel off) we group ONLY by root ad name and pull
    // the representative creative via the AGG_ONLY_MEASURES instead.
    const dimensions = new Set<string>(
      groupByMediaChannel
        ? [
            `${prefix}.media_channel`,
            `${prefix}.root_ad_name`,
            `${prefix}.creative_id`,
            `${prefix}.object_story_id`,
            `${prefix}.ad_id`,
            `${prefix}.destination_url`,
            `${prefix}.image_url`,
            `${prefix}.initiative_type`,
          ]
        : [`${prefix}.root_ad_name`],
    )

    // Add primary ID if needed
    const { primaryId, skipPrimaryId } =
      CREATIVE_REPORT_OPTIONS.find(({ value }) => value === source) ?? {}

    if (!skipPrimaryId && primaryId) {
      dimensions.add(`${prefix}.${primaryId}`)
    }

    const baseQuery = getBaseQuery(prefix, ALL_METRICS as TMetricsMapKey[])
    const q = {
      dimensions: Array.from(dimensions),
      ...baseQuery,
      measures: [
        ...(baseQuery.measures ?? []),
        `${prefix}.creative_id_agg`,
        ...(groupByMediaChannel
          ? []
          : AGG_ONLY_MEASURES.map(measure => `${prefix}.${measure}`)),
      ],
      offset: page * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE,
      total: true,
    } as Query

    // Add sorting
    if (sorting) {
      q.order = [[`${prefix}.${sorting.id}`, sorting.desc ? "desc" : "asc"]]
    }

    const dateRangeWithStarts: [Date, Date] = [
      dayjs(dateRange[0]).startOf("day").toDate(),
      dayjs(dateRange[1]).endOf("day").toDate(),
    ]

    // Apply time dimension and dep var filters
    const query = getQWithTimeDimensionAndDepVarFilters(
      q,
      prefix,
      dateRangeWithStarts,
      {
        depVar,
        depVarScaler,
      },
    )

    // Apply column filters
    if (cubeFilters) {
      query.filters = [...(query.filters ?? []), ...cubeFilters]
    }

    // Apply URL-based filters for campaign/adset names
    if (campaignNameFilter) {
      query.filters = [
        ...(query.filters ?? []),
        {
          member: `${prefix}.campaign_name`,
          operator: "equals" as BinaryOperator,
          values: [campaignNameFilter],
        },
      ]
    }

    if (adsetNameFilter) {
      query.filters = [
        ...(query.filters ?? []),
        {
          member: `${prefix}.adset_name`,
          operator: "equals" as BinaryOperator,
          values: [adsetNameFilter],
        },
      ]
    }

    return { query, prefix }
  }, [
    source,
    dateRange,
    page,
    columnFilters,
    sorting,
    depVar,
    depVarScaler,
    campaignNameFilter,
    adsetNameFilter,
    groupByMediaChannel,
  ])

  return {
    cubeQuery: cubeQuery.query,
    prefix: cubeQuery.prefix,
    depVar,
    depVarScaler,
    amazonEnabled,
    toggleAmazon,
  }
}
